import Layers from "slotworks/graphics/layers/layers";
import {Services} from "slotworks/services/services";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import Sequence from "slotworks/utils/contracts/sequence";
import {Easing, Tween} from "slotworks/utils/tween";
import * as TweenJS from "slotworks/utils/tween";
import graphicsManager from "slotworks/graphics/graphics-manager";
import Components from "slotworks/components/components";
import SGMatrixComponent from "./matrix/sg-matrix-component";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import canvasManager, {Orientation} from "slotworks/graphics/canvas/canvas-manager";
import sgBackgroundParticles from 'components/background/sg-background-particles';
import sgSandAnimation from "./sg-sand-animation";
import SgBetBarComponent from 'components/betbar/sg-bet-bar-component';

const LIGHTNING_ANIMATION_ID: string = "lightning_big";

type LightningTransform = {
    x: number;
    y: number;
    rotation: number;
    scale: number;
    anchor: {x: number, y: number;};
};

class SGWinRespinCelebrator {


    private lightningLandscapeTransform: LightningTransform[] = [
        {
            x: 251,
            y: 179,
            rotation: 3.61283,
            scale: 2,
            anchor: {x: 1, y: 0.5}
        },
        {
            x: 1711,
            y: 949,
            rotation: 0.4735,
            scale: 2,
            anchor: {x: 1, y: 0.5}
        }
    ];

    private lightningPortraitTransform: LightningTransform[] = [
        {
            x: -83,
            y: 378,
            rotation: 3.5604,
            scale: 1.7,
            anchor: {x: 1, y: 0.5}
        },
        {
            x: 1186,
            y: 973,
            rotation: 0.4363,
            scale: 1.7,
            anchor: {x: 1, y: 0.5}
        }
    ];

    private firstLightning: AnimatedSprite;
    private secondLightning: AnimatedSprite;
    private lightningAnimations: AnimatedSprite[] = [];
    private lightningDefaultAnimationSpeed: number = 0.25;
    private lightningRetrigerAnimationSpeed: number = 0.5;

    public startRespin(): Contract<void> {
        const text = Layers.CelebrationContent.getText("respin_text");
        const sprite = Layers.CelebrationContent.getSprite("glow");
        sprite.blendMode = PIXI.BLEND_MODES.SCREEN;
        this.setSpriteAnchorToCenter(sprite);
        return new Sequence([
            () => new Parallel([
                () => sgSandAnimation.playSandAnimation(),
                () => this.scaleIn(text),
                () => this.scaleIn(sprite),
                () => this.playThunder()
            ]),
            () => Contract.getTimeoutContract(250),
            () => new Parallel([
                () => new Sequence([
                    () => new Parallel([
                        () => this.animateAlpha(text),
                        () => this.animateAlpha(sprite)
                    ])
                ]),
                () => this.moveOut(text),
                () => this.moveOut(sprite)
            ])
        ]);
    }

    public startFreespinRetrigger(): Contract<void> {
        const text = Layers.Prompts.getText("winMessage");
        const sprite = Layers.Prompts.getSprite("glow");
        sprite.blendMode = PIXI.BLEND_MODES.SCREEN;
        this.setSpriteAnchorToCenter(sprite);
        const functions: Array<() => Contract<any>> = [];

        const countText = Layers.Prompts.getText("value");
        const count = parseInt(countText.text);
        for (let i = 1; i <= count; i++) {
            functions.push(() => new Parallel([
                () => this.playThunder(this.lightningRetrigerAnimationSpeed),
                () => Contract.wrap(() => {
                    text.text = `${Services.translations.get("fs.rtr", {VALUE: i, SPINS: i})}`;
                }),
                ()=> Contract.wrap(()=>{
                    (Components.betBar as SgBetBarComponent).addRemainingFreespins(1);                  
                })
            ]));
        }
        return new Parallel([
            () => this.scaleIn(text),
            () => this.scaleIn(sprite),
            () => new Sequence(functions)
        ]);
    }

    public endFreespinRetrigger(): Contract<void> {
        const text = Layers.Prompts.getText("winMessage");
        const sprite = Layers.Prompts.getSprite("glow");
        sprite.blendMode = PIXI.BLEND_MODES.SCREEN;
        return new Sequence([
            () => new Parallel([
                () => new Sequence([
                    () => new Parallel([
                        () => this.animateAlpha(text),
                        () => this.animateAlpha(sprite)
                    ])
                ]),
                () => this.moveOut(text),
                () => this.moveOut(sprite)
            ])
        ]);
    }

    private playThunder(animationSpeed: number = this.lightningDefaultAnimationSpeed): Contract<void> {
        const isLandscape: boolean = canvasManager.orientation === Orientation.LANDSCAPE;
        const transform: LightningTransform[] = isLandscape ? this.lightningLandscapeTransform : this.lightningPortraitTransform;
        canvasManager.onOrientationChange.add(() => this.repositionLightnings());
        return new Contract((resolve) => {
            this.firstLightning = this.getLightningAnimation(0, transform[0]);
            this.secondLightning = this.getLightningAnimation(1, transform[1]);
            this.firstLightning.animationSpeed = this.secondLightning.animationSpeed = animationSpeed;
            this.firstLightning.visible = this.secondLightning.visible = true;
            new Parallel([
                () => this.firstLightning.gotoAndPlayOnce(),
                () => this.secondLightning.gotoAndPlayOnce()
            ]).then(() => {
                this.firstLightning.visible = this.secondLightning.visible = false;
                resolve(null);
            });
            Services.sound.customEvent("win-respin");
        });
    }

    private repositionLightnings(): void {
        const isLandscape: boolean = canvasManager.orientation === Orientation.LANDSCAPE;
        const transform = isLandscape ? this.lightningLandscapeTransform : this.lightningPortraitTransform;
        if (this.lightningAnimations.length !== 0) {
            for (let i = 0; i < this.lightningAnimations.length; i++) {
                this.lightningAnimations[i].anchor.set(transform[i].anchor.x, transform[i].anchor.y);
                this.lightningAnimations[i].position.set(transform[i].x, transform[i].y);
                this.lightningAnimations[i].rotation = transform[i].rotation;
                this.lightningAnimations[i].scale.set(transform[i].scale);
            }
        }
    }

    private getLightningAnimation(index: number, transform: LightningTransform): AnimatedSprite {
        return this.lightningAnimations[index] || this.createLightning(index, transform);
    }

    private createLightning(index: number, transform: LightningTransform): AnimatedSprite {
        const lightningLayer: Layers = (Components.matrix as SGMatrixComponent).LightningLayer;
        const animation = graphicsManager.createAnimation(LIGHTNING_ANIMATION_ID);
        animation.anchor.set(transform.anchor.x, transform.anchor.y);
        animation.position.set(transform.x, transform.y);
        animation.rotation = transform.rotation;
        animation.scale.set(transform.scale);
        lightningLayer.add(animation);
        this.lightningAnimations[index] = animation;
        return animation;
    }

    private animateAlpha(object: PIXI.Text | PIXI.Sprite): Contract<void> {
        return new Contract((resolve) => {
            new Tween(object)
                .easing(Easing.Quartic.Out)
                .to({alpha: 0}, 500)
                .delay(400)
                .onComplete(() => resolve())
                .start();
        });
    }

    private scaleIn(object: PIXI.Text | PIXI.Sprite): Contract<void> {
        object.scale.set(0, 0);
        return new Contract((resolve) => {
            new TweenJS.Tween(object.scale)
                .to({x: 1, y: 1}, 500)
                .easing(TweenJS.Easing.Back.Out)
                .onComplete(() => resolve())
                .start();
        });
    }

    private moveOut(object: PIXI.Text | PIXI.Sprite): Contract<void> {
        return new Contract((resolve) => {
            new TweenJS.Tween(object)
                .to({y: 0}, 600)
                .easing(TweenJS.Easing.Back.In)
                .onComplete(() => resolve())
                .start();
        });
    }

    private setSpriteAnchorToCenter(sprite: PIXI.Sprite): void {
        sprite.anchor.set(0.5);
        sprite.x += sprite.width * 0.5;
        sprite.y += sprite.height * 0.5;
    }

}

export default new SGWinRespinCelebrator();
